import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { forwardRef, useMemo } from 'react';
import en from 'date-fns/locale/en-GB';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { useTheme } from 'styled-components';
import dayjs from '@/js/libs/dayjs';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import Icons from '../../../Elements/Icons';
import { YearSelect, MonthSelect, RangePresets } from '../components';
import { DatePickerInput } from '../components/DatePickerInput';
import { DATE_FORMAT, KEY_DATE_FORMAT, KEY_ENTER, DATE_RANGE_SEPARATOR, CALENDAR_CLASSNAME, CALENDAR_PORTAL_ID, DATE_PICKER_REF, } from '../constants';
import { getDateArray, getValidDateRange, isDefinedDateRange } from '../helpers';
import { usePreventCalendarCloseOnTouch } from '../hooks';
import { DatePickerStyles, DatePickerWrapper } from '../styled';
export const DatePickerRange = React.memo(forwardRef(function DatePickerRange({ dataTestId, dateFormat = [DATE_FORMAT, DATE_FORMAT], dateFormatKey = KEY_DATE_FORMAT, rangePresets, startDate, endDate, showLocal, onChange, onBlur, onKeyDown, value, error, warning, locale = 'en', isClearable, minDate, maxDate, ...props }, ref) {
    const { colors } = useTheme();
    const datePickerRef = React.useRef(null);
    registerLocale(locale, en);
    const { handleCalendarOpen, handleCalendarClose } = usePreventCalendarCloseOnTouch();
    const [selectedStartDate, selectedEndDate] = React.useMemo(() => {
        if (!value) {
            return [startDate, endDate];
        }
        return getValidDateRange({ value, dateFormatKey, showLocal, minDate, maxDate }) ?? [startDate, endDate];
    }, [value, startDate, endDate, dateFormatKey, showLocal, minDate, maxDate]);
    const handleKeyDown = useCallbackRef((e) => {
        if (onKeyDown) {
            onKeyDown(e);
        }
        else if (e.key === KEY_ENTER) {
            const range = getValidDateRange({
                value: e.currentTarget.value,
                dateFormatKey,
                showLocal,
                minDate,
                maxDate,
            });
            if (range) {
                onChange(range, e);
            }
        }
    });
    const handleBlur = useCallbackRef((e) => {
        const range = getValidDateRange({
            value: e.target.value,
            dateFormatKey,
            showLocal,
            minDate,
            maxDate,
        });
        if (range)
            onChange(range, e);
        onBlur?.(e);
    });
    const onClear = useCallbackRef(() => {
        onChange([null, null], undefined);
    });
    const handleRangeChange = useCallbackRef((value, e) => {
        onChange(value, e);
        datePickerRef.current?.setOpen(false);
    });
    React.useEffect(() => {
        if (rangePresets && !startDate && !endDate) {
            const defaultRange = rangePresets.find((item) => item.default === true);
            if (defaultRange?.dateRange && isDefinedDateRange(defaultRange.dateRange)) {
                onChange(defaultRange.dateRange, undefined);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const wrapOnChange = useCallbackRef((value, e) => {
        const range = getDateArray(value, showLocal);
        onChange(range, e);
    });
    const { selectedEndDateFormatted, selectedStartDateFormatted } = useMemo(() => ({
        selectedStartDateFormatted: selectedStartDate ? dayjs(selectedStartDate).startOf('day').utc().toDate() : null,
        selectedEndDateFormatted: selectedEndDate ? dayjs(selectedEndDate).startOf('day').utc().toDate() : null,
    }), [selectedEndDate, selectedStartDate]);
    return (_jsxs(_Fragment, { children: [_jsx(DatePickerStyles, {}), _jsx(ReactDatePicker, { popperClassName: CALENDAR_CLASSNAME, customInputRef: DATE_PICKER_REF, customInput: _jsx(DatePickerInput, { minWidth: 220, "data-testid": dataTestId, icon: isClearable && (!!startDate || !!endDate) ? (_jsx(Icons.Clear, { filled: true, size: 20, color: colors.grey70_1, onClick: onClear, "data-testid": 'icons-clear' })) : (_jsx(Icons.Calendar, { size: 20, color: colors.grey100_1 })), error: error, warning: warning, isActionIcon: isClearable && (!!startDate || !!endDate), ref: ref }), renderCustomHeader: ({ date, changeYear, changeMonth }) => (_jsxs(DatePickerWrapper, { children: [_jsx(MonthSelect, { date: date, changeMonth: changeMonth, locale: locale }), _jsx(YearSelect, { date: date, changeYear: changeYear, minYear: minDate?.getFullYear(), maxYear: maxDate?.getFullYear() })] })), value: value, onChange: wrapOnChange, startDate: selectedStartDateFormatted, endDate: selectedEndDateFormatted, dateFormat: dateFormat, locale: locale, onKeyDown: handleKeyDown, onBlur: handleBlur, selectsRange: true, placeholderText: `__.__.____ ${DATE_RANGE_SEPARATOR} __.__.____`, portalId: CALENDAR_PORTAL_ID, minDate: minDate, maxDate: maxDate, onCalendarOpen: handleCalendarOpen, onCalendarClose: handleCalendarClose, ref: datePickerRef, ...props, children: rangePresets && _jsx(RangePresets, { presets: rangePresets, onChange: handleRangeChange, startDate: startDate, endDate: endDate }) })] }));
}));
